<script setup lang="ts" generic="T extends string | number">
// Sierra's .seg: a pill track of uppercase choices, the chosen one filled with the
// accent. An exclusive choice, so it's a radio group: arrow keys move the choice.
// Options are values or { value, label, count? }; `label` names the group.
import { computed, ref } from 'vue';

const props = defineProps<{ modelValue: T; options: readonly (T | { value: T; label: string; count?: number })[]; label?: string }>();
const emit = defineEmits<{ 'update:modelValue': [value: T] }>();
const track = ref<HTMLElement | null>(null);

const normalized = computed(() =>
  props.options.map((option) => (typeof option === 'object' ? option : { value: option, label: String(option), count: undefined })),
);

function onKey(event: KeyboardEvent): void {
  const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
  if (step === undefined) return;
  event.preventDefault();
  const index = normalized.value.findIndex((option) => option.value === props.modelValue);
  const next = (index + step + normalized.value.length) % normalized.value.length;
  const option = normalized.value[next];
  if (option === undefined) return;
  emit('update:modelValue', option.value);
  track.value?.querySelectorAll<HTMLElement>('[role="radio"]')[next]?.focus();
}
</script>

<template>
  <div ref="track" class="itc-segment" role="radiogroup" :aria-label="label" @keydown="onKey">
    <button
      v-for="option in normalized"
      :key="option.value"
      type="button"
      role="radio"
      class="itc-segment__item"
      :aria-checked="option.value === modelValue"
      :tabindex="option.value === modelValue ? 0 : -1"
      @click="emit('update:modelValue', option.value)"
    >
      {{ option.label }}<b v-if="option.count !== undefined">{{ option.count }}</b>
    </button>
  </div>
</template>

<style scoped>
.itc-segment {
  display: flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid var(--itc-line);
  border-radius: var(--itc-r-pill);
  background: var(--itc-panel);
}
.itc-segment__item {
  flex: 1;
  min-height: 40px;
  padding: 0 12px;
  border: 0;
  border-radius: var(--itc-r-pill);
  background: transparent;
  color: var(--itc-muted);
  font-family: var(--itc-font-display);
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;
}
.itc-segment__item b {
  margin-left: 4px;
  font-family: var(--itc-font-mono);
  font-weight: 400;
  opacity: 0.75;
}
.itc-segment__item[aria-checked='true'] {
  background: var(--itc-accent);
  color: var(--itc-accent-ink);
}
</style>
